'use client'

import { useState } from 'react'
import { Sparkles, WandSparkles, CheckCircle2, Film, RefreshCcw } from 'lucide-react'
import type { HyperFramesPlan } from './types'
import { HyperFramesIframePreview } from './HyperFramesIframePreview'
import { cn } from '@/lib/utils'

type Props = {
  plan: HyperFramesPlan | null
  generating?: boolean
  regenerating?: boolean
  overlayMode?: boolean
  onGenerate?: () => void
  onRegenerateWithDirection?: (direction: string) => void
}

function getClipsCount(plan: HyperFramesPlan | null) {
  return Array.isArray(plan?.clips) ? plan.clips.length : 0
}

function getMotionBlocksCount(plan: HyperFramesPlan | null) {
  const clips = Array.isArray(plan?.clips) ? plan.clips.length : 0
  const blocks = Array.isArray((plan as any)?.motionBlueprint?.contentBlocks)
    ? (plan as any).motionBlueprint.contentBlocks.length
    : 0

  return Math.max(clips, blocks)
}

function getTransitionsCount(plan: HyperFramesPlan | null) {
  return Array.isArray(plan?.transitions) ? plan.transitions.length : 0
}

export function HyperFramesScenePanel({
  plan,
  generating = false,
  regenerating = false,
  overlayMode = false,
  onGenerate,
  onRegenerateWithDirection,
}: Props) {
  const [showDirectionBox, setShowDirectionBox] = useState(false)
  const [direction, setDirection] = useState('')

  const composition = plan?.composition || null
  const hasComposition = !!composition?.html || !!composition?.css || !!composition?.js
  const hasPlan = !!plan
  const busy = generating || regenerating

  function submitRegenerate() {
    const clean = direction.trim()

    if (!clean) return

    onRegenerateWithDirection?.(clean)
  }

  if (hasComposition && composition) {
    return (
        <div
        className={cn(
            'relative h-full w-full overflow-hidden',
            overlayMode ? 'bg-transparent' : 'bg-black'
        )}
        >
        <HyperFramesIframePreview composition={composition} />

        <div className="pointer-events-none absolute right-3 top-3 rounded-full border border-emerald-400/25 bg-emerald-400/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-100 backdrop-blur">
        Animación generada
        </div>

        <div className="absolute bottom-3 left-3 right-3 z-30 rounded-2xl border border-white/10 bg-black/55 p-3 backdrop-blur">
          {!showDirectionBox ? (
            <button
              type="button"
              onClick={() => setShowDirectionBox(true)}
              disabled={busy}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-xs font-medium text-white disabled:opacity-40"
            >
              <RefreshCcw className="h-3.5 w-3.5" />
              Ajustar animación
            </button>
          ) : (
            <div className="space-y-2">
              <textarea
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
                rows={3}
                placeholder="Ejemplo: haz el segundo momento como una interfaz de audio con ondas, no como avatar."
                className="w-full resize-none rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-xs text-white outline-none placeholder:text-white/35"
              />

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowDirectionBox(false)
                    setDirection('')
                  }}
                  disabled={busy}
                  className="flex-1 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/70 disabled:opacity-40"
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  onClick={submitRegenerate}
                  disabled={busy || !direction.trim()}
                  className="flex-1 rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-40"
                >
                  {regenerating ? 'Ajustando…' : 'Aplicar ajuste'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (!hasPlan) {
    return (
        <div
            className={cn(
            'flex h-full w-full items-center justify-center p-8 text-center',
            overlayMode
                ? 'items-end justify-center bg-transparent p-4'
                : 'bg-primary/5'
            )}
        >
        <div className="max-w-[280px]">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
            <Film className="h-6 w-6" />
          </div>

          <div className={cn('text-lg font-semibold', overlayMode ? 'text-white' : 'text-foreground')}>
            Animación pendiente
          </div>

          <div className={cn('mt-2 text-sm leading-6', overlayMode ? 'text-white/70' : 'text-muted-foreground')}>
            Primero prepara la animación visual para esta escena.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full w-full items-center justify-center bg-primary/5 p-8 text-center">
      <div
        className={cn(
            'max-w-[310px]',
            overlayMode &&
            'rounded-3xl border border-white/10 bg-black/60 p-4 text-white shadow-2xl backdrop-blur-xl'
        )}
        >
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-emerald-400/25 bg-emerald-400/10 text-emerald-300">
          <Sparkles className="h-6 w-6" />
        </div>

        <div className={cn('text-lg font-semibold', overlayMode ? 'text-white' : 'text-foreground')}>
          Animación lista para crear
        </div>

        <div className={cn('mt-2 text-sm leading-6', overlayMode ? 'text-white/70' : 'text-muted-foreground')}>
          Esta escena ya tiene una estructura visual preparada. Ahora genera la animación que se usará como capa encima del visual base.
        </div>

        <div className="mt-5 grid grid-cols-3 gap-2">
          <div
            className={cn(
                'rounded-2xl border p-3',
                overlayMode
                ? 'border-white/10 bg-white/10'
                : 'border-border/70 bg-background/70'
            )}
            >
            <div className={cn('text-base font-semibold', overlayMode ? 'text-white' : 'text-foreground')}>
              {getClipsCount(plan)}
            </div>
            <div className={cn('text-[10px]', overlayMode ? 'text-white/55' : 'text-muted-foreground')}>
              Clips
            </div>
          </div>

          <div
            className={cn(
                'rounded-2xl border p-3',
                overlayMode
                ? 'border-white/10 bg-white/10'
                : 'border-border/70 bg-background/70'
            )}
            >
            <div className={cn('text-base font-semibold', overlayMode ? 'text-white' : 'text-foreground')}>
            {getMotionBlocksCount(plan)}
            </div>
            <div className={cn('text-[10px]', overlayMode ? 'text-white/55' : 'text-muted-foreground')}>
            Elementos
            </div>
          </div>

          <div
            className={cn(
                'rounded-2xl border p-3',
                overlayMode
                ? 'border-white/10 bg-white/10'
                : 'border-border/70 bg-background/70'
            )}
            >
            <div className={cn('text-base font-semibold', overlayMode ? 'text-white' : 'text-foreground')}>
              {getTransitionsCount(plan)}
            </div>
            <div className={cn('text-[10px]', overlayMode ? 'text-white/55' : 'text-muted-foreground')}>
              Trans.
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onGenerate}
          disabled={busy}
          className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-40"
        >
          {generating ? (
            <>
              <WandSparkles className="h-4 w-4 animate-pulse" />
              Generando animación…
            </>
          ) : (
            <>
              <CheckCircle2 className="h-4 w-4" />
              Generar animación
            </>
          )}
        </button>
      </div>
    </div>
  )
}